import { useEffect, useState } from 'react';
import { Clock, AlertCircle, Star } from 'lucide-react';
import { apigetHomeTestData, apiSubmitTestResult } from '../../services/apihomeTestseries/apihometestseries';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import useAuth from '../../services/store/useAuth';
import Warningpage from './Warningpage';
import apiurl from '../../services/apiendpoint/apiendpoint';
 

export default function Testpage() {

  const { slug, testId } = useParams();
  const { userdetails } = useAuth();
  const [showAgreement, setShowAgreement] = useState(true);
  const [testStarted, setTestStarted] = useState(false);
  const [testData, setTestData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [markedForReview, setMarkedForReview] = useState(new Set());
  const [timeLeft, setTimeLeft] = useState(0);
  const [activeSubject, setActiveSubject] = useState('');
  const [loading, setLoading] = useState(true);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [currentSubjectQuestionIndex, setCurrentSubjectQuestionIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getSubjectQuestions = (subjectName) => {
    if (!testData) return [];
    
    if (!testData.has_subjects || !testData.subjects || testData.subjects.length === 0) {
      return testData.questions || [];
    }
    
    return testData.questions.filter(q => q.subject === subjectName);
  };


  useEffect(() => {
    const fetchTestData = async () => {
      try {
        const res = await apigetHomeTestData(slug);
        console.log('API Response:', res);
        
        if (res?.resdata) {
          const testSeries = Array.isArray(res.resdata) ? res.resdata[0] : res.resdata;
          
          if (!testSeries) {
            console.error('No test data found');
            toast.error('Test data not found');
            setLoading(false);
            return;
          }
          
          const selectedTest = testSeries.tests?.find(t => {
            const id = t._id?.$oid || t._id;
            return id === testId;
          });
          
          if (selectedTest) {
            setTestData(selectedTest);
            setTimeLeft(selectedTest.duration * 60);
            
            if (selectedTest.has_subjects && selectedTest.subjects?.length > 0) {
              setActiveSubject(selectedTest.subjects[0].subject_name);
            } else {
              setActiveSubject('General');
            }
          } else {
            console.error('Test not found with ID:', testId);
            toast.error('Test not found');
          }
        }
      } catch (err) {
        console.error('Fetch error:', err);
        toast.error('Failed to load test data');
      } finally {
        setLoading(false);
      }
    };

    fetchTestData();
  }, [slug, testId]);

  useEffect(() => {
    if (!testStarted || timeLeft <= 0 || !testData) return;
    
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmitTest();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [testStarted, timeLeft, testData]);

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStartTest = () => {
    setShowAgreement(false);
    setTestStarted(true);
  };

  const handleAnswerSelect = (questionId, optionIndex) => {
    setAnswers(prev => ({ ...prev, [questionId]: `option_${optionIndex}` }));
  };

  const handleMarkForReview = () => {
    if (!currentQuestion) return;
    const questionId = currentQuestion._id;
    setMarkedForReview(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  const handleClearResponse = () => {
    if (!currentQuestion) return;
    const questionId = currentQuestion._id;
    setAnswers(prev => {
      const newAnswers = { ...prev };
      delete newAnswers[questionId];
      return newAnswers;
    });
  };

const [testStartTime] = useState(new Date());

const handleSubmitTest = async () => {
    const attempted = Object.keys(answers).length;
    const total = testData.questions.length;
    const unattempted = total - attempted;
    
    try {
        const testResultData = {
            test_series_slug: slug,
            test_id: testId,
            answers: answers,
            marked_for_review: Array.from(markedForReview),
            start_time: testStartTime,
            end_time: new Date(),
            time_taken: testData.duration * 60 - timeLeft,
            user_id: userdetails.id,
            user_type: userdetails.Role === 'Customer' ? 'customers' : 'StudentApplication'
        };
        
        const result = await apiSubmitTestResult(testResultData);
        toast.success(`Test Submitted Successfully!`);
        setShowSubmitConfirm(false);
        navigate(`/test-result`, {
          state: {
            attemptId: result.attemptId, 
            testSeriesSlug: slug,
          }
        });
        
    } catch (err) {
        toast.error("Failed to submit test. Please try again.");
        console.error(err);
    }
};

  const getQuestionStatus = (question) => {
      const answer = answers[question._id];
      const isAnswered = answer !== undefined && answer !== null && 
                        (typeof answer === 'object' ? answer.option !== '' : answer !== '');
      const isMarked = markedForReview.has(question._id);
      
      if (isAnswered && isMarked) return 'answered-marked';
      if (isAnswered) return 'answered';
      if (isMarked) return 'marked';
      return 'not-visited';
  };

  const getSubjectStats = (subjectName) => {
      const subjectQuestions = testData.questions.filter(q => q.subject === subjectName);
      const attempted = subjectQuestions.filter(q => {
        const answer = answers[q._id];
        return answer !== undefined && answer !== null && 
              (typeof answer === 'object' ? answer.option !== '' : answer !== '');
      }).length;
      const marked = subjectQuestions.filter(q => markedForReview.has(q._id) && !answers[q._id]).length;
      const answeredMarked = subjectQuestions.filter(q => {
        const answer = answers[q._id];
        const isAnswered = answer !== undefined && answer !== null && 
                          (typeof answer === 'object' ? answer.option !== '' : answer !== '');
        return isAnswered && markedForReview.has(q._id);
      }).length;
      const notVisited = subjectQuestions.filter(q => !answers[q._id] && !markedForReview.has(q._id)).length;
      
      return { marked, attempted, notVisited, answeredMarked };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"></div>
          <p className="text-lg font-medium text-gray-700">Loading test...</p>
        </div>
      </div>
    );
  }

  if (!testData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
          <p className="text-xl font-semibold text-gray-800">Test not found</p>
        </div>
      </div>
    );
  }

  const subjectQuestions = getSubjectQuestions(activeSubject);
  const currentQuestion = subjectQuestions[currentSubjectQuestionIndex];
  const currentStatus = currentQuestion ? getQuestionStatus(currentQuestion) : 'not-visited';

  if (showAgreement) {
    return (
      <Warningpage testData={testData} handleStartTest={handleStartTest} />
    );
  }

  if (!testStarted) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="sticky top-0 z-40 bg-white border-b-2 border-blue-100 shadow-md">
        <div className="max-w-[95rem] mx-auto px-6 py-2">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                timeLeft < 120 ? 'bg-red-100' : 'bg-blue-100'
              }`}>
                <Clock className={timeLeft < 120 ? 'text-red-600' : 'text-blue-600'} size={20} />
                <span className="text-sm font-medium text-gray-600">Time Left:</span>
                <span className={`text-xl font-bold ${
                  timeLeft < 120 ? 'text-red-600' : 'text-blue-600'
                }`}>
                  {formatTime(timeLeft)}
                </span>
              </div>
            </div>
            
            <h1 className="text-xl font-bold text-gray-800">{testData.test_name}</h1>
            
            <button onClick={() => setShowSubmitConfirm(true)}
              className="px-6 py-2 font-semibold text-white transition-all duration-200 rounded-lg shadow-md bg-regal-blue hover:shadow-lg"
            >
              Submit Test
            </button>
          </div>
        </div>
      </div>

     <div className={`max-w-[110rem] mx-auto p-3 lg:flex gap-6 ${isMobile ? 'pb-32' : ''}`}>
        <div className="flex-1 space-y-4">
          <div className="bg-white shadow-md rounded-xl">
            <div className="flex overflow-x-scroll border-b slim-scrollbar-test">
            {testData.has_subjects && testData.subjects && testData.subjects.length > 0 && (
              <div className="flex overflow-x-scroll border-b slim-scrollbar-test">
                {testData.subjects.map(subject => {
                  const stats = getSubjectStats(subject.subject_name);
                  const total = stats.marked + stats.attempted + stats.notVisited + stats.answeredMarked;
                  const completed = stats.attempted + stats.answeredMarked;
                  
                  return (
                    <button key={subject.subject_name}
                      onClick={() => {
                        setActiveSubject(subject.subject_name);
                        setCurrentSubjectQuestionIndex(0);
                      }}
                      className={`flex-1 lg:px-6 lg:py-4 px-3 py-2 font-semibold transition-all ${
                        activeSubject === subject.subject_name ? 
                        'text-blue-600 bg-blue-50 border-b-3 border-blue-600' : 
                        'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
                      }`}
                      style={{ unicodeBidi: 'plaintext' }}
                    >
                      <div className="text-center">
                        <div>{subject.subject_name || 'Subject'}</div>
                        <div className="mt-1 text-xs text-gray-500">
                          {completed}/{total} answered
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            </div>

            <div className="p-8">
              <div className="mb-8">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-xl font-semibold leading-relaxed text-gray-800">
                    Question {currentSubjectQuestionIndex + 1} of {subjectQuestions.length}
                  </h3>
                  <div className="flex gap-2">
                    {currentStatus === 'answered' && (
                      <span className="px-3 py-1 text-xs font-semibold text-green-700 bg-green-100 rounded-full">
                        Answered
                      </span>
                    )}
                    {currentStatus === 'marked' && (
                      <span className="px-3 py-1 text-xs font-semibold rounded-full text-amber-700 bg-amber-100">
                        Marked for Review
                      </span>
                    )}
                    {currentStatus === 'answered-marked' && (
                      <span className="px-3 py-1 text-xs font-semibold text-blue-700 bg-blue-100 rounded-full">
                        Answered & Marked
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-lg leading-relaxed text-gray-700" style={{ unicodeBidi: 'plaintext', direction: 'ltr' }}>
                  {currentQuestion?.question_text}
                </p>
                 {currentQuestion?.diagram && currentQuestion.diagram !== '' && (
                  <div className="mt-4">
                    <img src={`${apiurl()}/${currentQuestion.diagram}`} alt="Question diagram" className="object-cover object-center max-w-full rounded-lg shadow-md h-60"
                      onError={(e) => {e.target.style.display = 'none'; console.error('Failed to load diagram image');}}
                    />
                  </div>
                )}
              </div>

            <div className="space-y-3">
              {currentQuestion?.options.map((option, idx) => {
                const optionLabel = ['A', 'B', 'C', 'D'][idx];
                const hasImage = currentQuestion.option_images && 
                                currentQuestion.option_images[idx] && 
                                currentQuestion.option_images[idx].trim() !== '';
                
                const optionValue = hasImage ? optionLabel : option;
                const isSelected = answers[currentQuestion._id] === `option_${idx}`;
                
                return (
                  <button key={idx} 
                    onClick={() => handleAnswerSelect(currentQuestion._id, idx)}
                    className={`w-full text-left p-5 rounded-xl border-2 transition-all duration-200 ${
                      isSelected
                        ? 'border-blue-500 bg-blue-100 shadow-md'
                        : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                        isSelected ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600'}`}>
                        {optionLabel}
                      </span>
                      {hasImage ? (
                        <img src={`${apiurl()}/${currentQuestion.option_images[idx]}`} alt={`Option ${optionLabel}`} className="flex-1 object-contain rounded-lg max-h-12"
                          onError={(e) => { e.target.style.display = 'none';  console.error('Failed to load option image'); }}
                        />
                      ) : (
                        <span className="flex-1 text-gray-700" style={{ unicodeBidi: 'plaintext', direction: 'ltr' }}>{option}</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            </div>

            <div className={`grid grid-cols-2 gap-3 p-6 border-t bg-gray-50 rounded-b-xl ${
              isMobile ? 'hidden' : '2xl:flex sm:grid sm:grid-cols-2 md:grid md:grid-cols-4 md:gap-3 lg:grid lg:grid-cols-4 lg:gap-3'
            }`}>
              <button onClick={() => setCurrentSubjectQuestionIndex(Math.max(0, currentSubjectQuestionIndex - 1))} disabled={currentSubjectQuestionIndex === 0}
                className="px-6 py-2 font-semibold text-white transition-all duration-200 bg-gray-600 rounded-lg hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                ← Previous
              </button>
                
              <button onClick={handleMarkForReview} className="px-6 py-2 font-semibold transition-all duration-200 rounded-lg text-amber-700 bg-amber-100 hover:bg-amber-200">
                <Star className="inline mr-2" size={18} />
                {markedForReview.has(currentQuestion?._id) ? 'Unmark' : 'Mark for Review'}
              </button>
              
              <button onClick={handleClearResponse} className="px-6 py-2 font-semibold text-gray-700 transition-all duration-200 bg-gray-200 rounded-lg hover:bg-gray-300">
                Clear Response
              </button>
              
              <button onClick={() => setCurrentSubjectQuestionIndex(Math.min(subjectQuestions.length - 1, currentSubjectQuestionIndex + 1))}
                disabled={currentSubjectQuestionIndex === subjectQuestions.length - 1}
                className="px-6 py-2 font-semibold text-white transition-all duration-200 bg-blue-600 rounded-lg 2xl:ml-auto hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next →
              </button>
            </div>
            {isMobile && (
              <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t-2 border-gray-200 shadow-lg">
                <div className="grid grid-cols-2 gap-2 p-3">
                  <button onClick={() => setCurrentSubjectQuestionIndex(Math.max(0, currentSubjectQuestionIndex - 1))} 
                    disabled={currentSubjectQuestionIndex === 0}
                    className="px-4 py-3 text-sm font-semibold text-white transition-all duration-200 bg-gray-600 rounded-lg hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    ← Previous
                  </button>
                  
                  <button onClick={() => setCurrentSubjectQuestionIndex(Math.min(subjectQuestions.length - 1, currentSubjectQuestionIndex + 1))}
                    disabled={currentSubjectQuestionIndex === subjectQuestions.length - 1}
                    className="px-4 py-3 text-sm font-semibold text-white transition-all duration-200 bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next →
                  </button>
                  
                  <button onClick={handleMarkForReview} 
                    className="px-4 py-3 text-sm font-semibold transition-all duration-200 rounded-lg text-amber-700 bg-amber-100 hover:bg-amber-200"
                  >
                    <Star className="inline mr-1" size={16} />
                    {markedForReview.has(currentQuestion._id) ? 'Unmark' : 'Mark'}
                  </button>
                  
                  <button onClick={handleClearResponse} 
                    className="px-4 py-3 text-sm font-semibold text-gray-700 transition-all duration-200 bg-gray-200 rounded-lg hover:bg-gray-300"
                  >
                    Clear
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-5 space-y-4 lg:w-96 lg:mt-0">
          <div className="p-6 bg-white shadow-md rounded-xl">
              <h3 className="mb-4 text-lg font-bold text-gray-800">
                Question Palette {testData.has_subjects && activeSubject ? `- ${activeSubject}` : ''}
              </h3>
            
          <div className="grid grid-cols-6 gap-2 mb-6">
              {subjectQuestions.map((q, idx) => {
                const status = getQuestionStatus(q);
                
                return (
                  <button key={q._id} onClick={() => setCurrentSubjectQuestionIndex(idx)}
                    className={` h-10 w-10 rounded-lg font-semibold text-sm transition-all duration-200 ${idx === currentSubjectQuestionIndex? 'ring-2 ring-blue-600 ring-offset-2' : ''
                    } ${
                      status === 'answered'
                        ? 'bg-green-500 text-white hover:bg-green-600'
                        : status === 'marked'
                        ? 'bg-amber-500 text-white hover:bg-amber-600'
                        : status === 'answered-marked'
                        ? 'bg-blue-500 text-white hover:bg-blue-600'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {idx + 1}
                  </button>
                );
              })}
            </div>


            <div className="pt-4 space-y-3 text-sm border-t">
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 bg-green-500 rounded-lg"></div>
                <span className="text-gray-700">Answered</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-lg bg-amber-500"></div>
                <span className="text-gray-700">Marked for Review</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 bg-blue-500 rounded-lg"></div>
                <span className="text-gray-700">Answered & Marked</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 bg-gray-200 rounded-lg"></div>
                <span className="text-gray-700">Not Visited</span>
              </div>
            </div>
          </div>

        {testData.has_subjects && testData.subjects && testData.subjects.length > 0 && testData.subjects.map(subject => {
          const stats = getSubjectStats(subject.subject_name);
          const total = stats.marked + stats.attempted + stats.notVisited + stats.answeredMarked;
            
            return (
              <div key={subject.subject_name} className="p-5 bg-white shadow-md rounded-xl">
                <h4 className="mb-3 font-bold text-gray-800" style={{ unicodeBidi: 'plaintext' }}>{subject.subject_name}</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Answered:</span>
                    <span className="font-semibold text-green-600">{stats.attempted}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Marked:</span>
                    <span className="font-semibold text-amber-600">{stats.marked}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Not Visited:</span>
                    <span className="font-semibold text-gray-600">{stats.notVisited}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t">
                    <span className="font-semibold text-gray-700">Total:</span>
                    <span className="font-bold text-gray-900">{total}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showSubmitConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="w-full max-w-md p-8 bg-white shadow-2xl rounded-2xl">
            <h3 className="mb-4 text-2xl font-bold text-gray-800">Submit Test?</h3>
            <div className="mb-6 space-y-2 text-gray-600">
              <p>Total Questions: <strong>{testData.questions.length}</strong></p>
              <p>Attempted: <strong className="text-green-600">{Object.keys(answers).length}</strong></p>
              <p>Unattempted: <strong className="text-red-600">{testData.questions.length - Object.keys(answers).length}</strong></p>
            </div>
            <p className="mb-6 text-gray-700">Are you sure you want to submit the test?</p>
            <div className="flex gap-4">
              <button onClick={() => setShowSubmitConfirm(false)} className="flex-1 px-6 py-3 font-semibold text-gray-700 transition-all duration-200 bg-gray-200 rounded-lg hover:bg-gray-300">
                Cancel
              </button>
              <button onClick={handleSubmitTest} className="flex-1 px-6 py-3 font-semibold text-white transition-all duration-200 bg-green-600 rounded-lg hover:bg-green-700">
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}